/**
 * Auto-generated from dark-roast/api-envoy OpenAPI spec. Do not edit manually.
 * Regenerate with: yarn sync-entities
 */

import { Api } from '@axiom/ui';
import { type User, OpportunityEntity } from '@axiom/domain';
import { CreateUsersResponseEntity } from './entities/create-users-response-entity';
import { ReadOpportunitiesInterviewsResponseEntity } from './entities/read-opportunities-interviews-response-entity';
import { ReadOpportunitiesSearchResponseEntity } from './entities/read-opportunities-search-response-entity';
import { ReadUserMeResponseEntity } from './entities/read-user-me-response-entity';
import { ReadUserMeSettingsInfoResponseEntity } from './entities/read-user-me-settings-info-response-entity';
import { ReadUsersSearchResponseEntity } from './entities/read-users-search-response-entity';
import { UpdateUserSettingResponseEntity } from './entities/update-user-setting-response-entity';

import { type CreateUsersRequest } from './dtos/create-users-request';
import { type CreateUsersResendUserWelcomeEmailRequest } from './dtos/create-users-resend-user-welcome-email-request';
import { type CreateUsersResponse } from './dtos/create-users-response';
import { type CreateUsersSearchesRequest } from './dtos/create-users-searches-request';
import { type ReadCandidatesReputationDetailsRequest } from './dtos/read-candidates-reputation-details-request';
import { type ReadCandidatesReputationDetailsResponse } from './dtos/read-candidates-reputation-details-response';
import { type ReadOpportunitiesInterviewsResponse } from './dtos/read-opportunities-interviews-response';
import { type ReadUserMeResponse } from './dtos/read-user-me-response';
import { type ReadUserMeSettingsInfoResponse } from './dtos/read-user-me-settings-info-response';
import { type ReadUsersNotificationsInlineResponse } from './dtos/read-users-notifications-inline-response';
import { type ReadUsersOpportunitiesInterviewsRequest } from './dtos/read-users-opportunities-interviews-request';
import { type ReadUsersOpportunitiesSearchRequest } from './dtos/read-users-opportunities-search-request';
import { type ReadUsersOpportunitiesSearchResponse } from './dtos/read-users-opportunities-search-response';
import { type ReadUsersSearchResponse } from './dtos/read-users-search-response';
import { type UpdateUserSearchDto } from './dtos/update-user-search-dto';
import { type UpdateUserSettingRequest } from './dtos/update-user-setting-request';
import { type UpdateUserSettingResponse } from './dtos/update-user-setting-response';
import { type UpdateUsersSearchesRequest } from './dtos/update-users-searches-request';
import { EnvUtil } from '../../utils/env-util';

class UserApiClass extends Api {
  constructor() {
    super({ domain: EnvUtil.apiEnvoyUrl });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /user/me
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: UserController.getUserMe — GET /user/me */
  readUserMe() {
    return super.read<{ "data": ReadUserMeResponse }>({
      endpoint: '/user/me',
      method: 'GET',
      schema: { "data": ReadUserMeResponseEntity },
    });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /user/me/settingsInfo
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: EnvoyUserController.getUserMe — GET /user/me/settingsInfo */
  readUserMeSettingsInfo() {
    return super.read<{ "data": ReadUserMeSettingsInfoResponse }>({
      endpoint: '/user/me/settingsInfo',
      method: 'GET',
      schema: { "data": ReadUserMeSettingsInfoResponseEntity },
    });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /user/settings/{name}
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: UserSettingsController.putUserSetting — PUT /user/settings/{name} */
  updateUserSetting({ name, body }: { name: string; body: UpdateUserSettingRequest }) {
    return super.write<{ "data": UpdateUserSettingResponse }>({
      endpoint: `/user/settings/${name}`,
      method: 'PUT',
      body,
      schema: { "data": UpdateUserSettingResponseEntity },
    });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /users
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: UsersController.createUser — POST /users */
  createUsers({ body }: { body: CreateUsersRequest }) {
    return super.write<{ "data": CreateUsersResponse }>({
      endpoint: '/users',
      method: 'POST',
      body,
      schema: { "data": CreateUsersResponseEntity },
    });
  }

  /** Java: UsersController.patchUser — PATCH /users/{id} */
  updateUser({ id, body }: { id: User['id']; body: Partial<CreateUsersResponse> }) {
    return super.write<{ "data": CreateUsersResponse }>({
      endpoint: `/users/${id}`,
      method: 'PATCH',
      body,
      schema: { "data": CreateUsersResponseEntity },
    });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /users/{id}/notifications/inline
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: UserNotificationsController.getInlineNotifications — GET /users/{id}/notifications/inline */
  readUsersNotificationsInline({ id }: { id: User['id'] }) {
    return super.read<{ "data": ReadUsersNotificationsInlineResponse }>({
      endpoint: `/users/${id}/notifications/inline`,
      method: 'GET',
    });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /users/{id}/notifications/inline/acknowledge
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: UserNotificationsController.acknowledgeInlineNotifications — POST /users/{id}/notifications/inline/acknowledge */
  createUsersNotificationsInlineAcknowledge({ id, body }: { id: User['id']; body: Array<string> }) {
    return super.write<Record<string, never>>({
      endpoint: `/users/${id}/notifications/inline/acknowledge`,
      method: 'POST',
      body,
    });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /users/{id}/opportunities/interviews
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: EnvoyUsersController.searchUserSpecificOpportunityInterviews — POST /users/{id}/opportunities/interviews */
  readUsersOpportunitiesInterviews({ id, body }: { id: User['id']; body: ReadUsersOpportunitiesInterviewsRequest }) {
    return super.read<ReadOpportunitiesInterviewsResponse>({
      endpoint: `/users/${id}/opportunities/interviews`,
      method: 'POST',
      body,
      schema: { "data": [ReadOpportunitiesInterviewsResponseEntity] },
    });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /users/{id}/opportunities/search
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: EnvoyUsersController.searchUserOpportunities — POST /users/{id}/opportunities/search */
  readUsersOpportunitiesSearch({ id, body }: { id: User['id']; body: ReadUsersOpportunitiesSearchRequest }) {
    return super.read<ReadUsersOpportunitiesSearchResponse>({
      endpoint: `/users/${id}/opportunities/search`,
      method: 'POST',
      body,
      schema: { "data": [ReadOpportunitiesSearchResponseEntity] },
    });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /users/{id}/reputationDetails
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: ReputationDetailsController.reputationDetailsSearch — POST /users/{id}/reputationDetails */
  readUsersReputationDetails({ id, body }: { id: User['id']; body: ReadCandidatesReputationDetailsRequest }) {
    return super.read<ReadCandidatesReputationDetailsResponse>({
      endpoint: `/users/${id}/reputationDetails`,
      method: 'POST',
      body,
      schema: { "data": [{ "opportunity": OpportunityEntity }] },
    });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /users/resendUserWelcomeEmail
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: EnvoyUsersController.resendUserWelcomeEmail — POST /users/resendUserWelcomeEmail */
  createUsersResendUserWelcomeEmail({ body }: { body: CreateUsersResendUserWelcomeEmailRequest }) {
    return super.write<Record<string, never>>({
      endpoint: '/users/resendUserWelcomeEmail',
      method: 'POST',
      body,
    });
  }

  /*
   * ────────────────────────────────────────────────────────────────────
   *   /users/searches
   * ────────────────────────────────────────────────────────────────────
   */

  /** Java: UserSearchesController.getUserSearches — GET /users/searches */
  readUsersSearches({ query }: { query: { "type": "candidate" | "opportunity" } }) {
    return super.read<{ "data": Array<ReadUsersSearchResponse> }>({
      endpoint: '/users/searches',
      method: 'GET',
      query,
      schema: { "data": [ReadUsersSearchResponseEntity] },
    });
  }

  /** Java: UserSearchesController.createUserSearches — POST /users/searches */
  createUsersSearches({ body }: { body: CreateUsersSearchesRequest }) {
    return super.write<Record<string, never>>({
      endpoint: '/users/searches',
      method: 'POST',
      body,
    });
  }

  /** Java: UserSearchesController.updateUserSearches — PATCH /users/searches */
  updateUsersSearches({ body }: { body: UpdateUsersSearchesRequest }) {
    return super.write<Record<string, never>>({
      endpoint: '/users/searches',
      method: 'PATCH',
      body,
    });
  }

  /** Java: UserSearchesController.getUserSearchById — GET /users/searches/{id} */
  readUsersSearch({ id }: { id: User['id'] }) {
    return super.read<{ "data": ReadUsersSearchResponse }>({
      endpoint: `/users/searches/${id}`,
      method: 'GET',
      schema: { "data": ReadUsersSearchResponseEntity },
    });
  }

  /** Java: UserSearchesController.updateUserSearch — PATCH /users/searches/{id} */
  updateUsersSearch({ id, body }: { id: User['id']; body: UpdateUserSearchDto }) {
    return super.write<Record<string, never>>({
      endpoint: `/users/searches/${id}`,
      method: 'PATCH',
      body,
    });
  }

  /** Java: UserSearchesController.deleteUserSearch — DELETE /users/searches/{id} */
  deleteUsersSearch({ id }: { id: User['id'] }) {
    return super.write<Record<string, never>>({
      endpoint: `/users/searches/${id}`,
      method: 'DELETE',
    });
  }
}

export const UserApi = new UserApiClass();
export type { UserApiClass };
